import { Recorder, type RecorderOptions, type RecorderResult } from '@ohear/audio'
/**
 * React hook wrapping the @ohear/audio Recorder.
 *
 * Owns the Recorder lifecycle for a page: lazy-creates on first use, releases
 * the mic on unmount. Exposes a small imperative API (`start`, `stop`,
 * `cancel`) plus reactive state for the UI (recording flag, live peak,
 * permission denied error).
 *
 * The hook does NOT interact with the upload queue or the recording state
 * machine — `useRecordingMachine` orchestrates those, calling into here for
 * the raw audio capture.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { MockRecorder, type RecorderLike, isTestMode } from './MockRecorder'

/**
 * URL for the AudioWorklet module. The audio package publishes the worklet as
 * a TypeScript source — the consuming app is responsible for getting it to a
 * URL the browser can load via `audioWorklet.addModule()`.
 *
 * Vite's `?worker&url` query bundles the module and returns its asset URL.
 * If the env doesn't support that syntax (SSR, tests), the Recorder will
 * still throw a clear error at start() time.
 */
// Vite's `?worker&url` import yields the bundled worklet's asset URL.
// Declared in `vite-env.d.ts` so tsc accepts the query suffix.
import recorderWorkletUrl from '@ohear/audio/src/worklets/recorder.worklet.ts?worker&url'

/**
 * Construct a recorder. In e2e mode (`window.__OHEAR_TEST_MODE__ === true`)
 * returns a {@link MockRecorder} that synthesizes a sine wave so tests don't
 * need real microphone access. In all other contexts returns the production
 * `Recorder`. The mock is gated by a runtime flag — production bundles never
 * activate it because the flag is never set.
 */
function createRecorder(cfg: RecorderOptions): RecorderLike {
  if (isTestMode()) return new MockRecorder(cfg)
  return new Recorder(cfg)
}

export interface UseRecorderOptions {
  /** Sample rate in Hz. OHEAR studio standard is 48000. */
  sampleRate?: number
  /** When true, recording auto-cancels on unmount. Default true. */
  cancelOnUnmount?: boolean
}

export interface UseRecorderApi {
  start: () => Promise<void>
  stop: () => Promise<RecorderResult>
  cancel: () => void
  /** True between start() resolving and stop()/cancel() returning. */
  isRecording: boolean
  /**
   * True once the recorder is initialised and ready to record. The shell
   * disables the REC button until this flips so users can't trigger a long
   * AudioWorklet load with no feedback.
   */
  isReady: boolean
  /** Live peak [0,1] observed since current start(). */
  livePeak: number
  /** Last error from start() (e.g. mic permission denied). */
  error: Error | null
  /** Reset the live peak counter (UI calls between takes for a clean meter). */
  resetPeak: () => void
}

export function useRecorder(opts: UseRecorderOptions = {}): UseRecorderApi {
  const sampleRate = opts.sampleRate ?? 48000
  const cancelOnUnmount = opts.cancelOnUnmount ?? true
  const recorderRef = useRef<RecorderLike | null>(null)
  const [isRecording, setIsRecording] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [livePeak, setLivePeak] = useState(0)
  const [error, setError] = useState<Error | null>(null)
  const peakRafRef = useRef<number | null>(null)

  const ensureRecorder = useCallback((): RecorderLike => {
    if (recorderRef.current) return recorderRef.current
    const cfg: RecorderOptions = {
      sampleRate,
      bitDepth: 24,
      refVoiceMuteOnRecord: true,
      workletUrl: recorderWorkletUrl,
    }
    const r = createRecorder(cfg)
    recorderRef.current = r
    return r
  }, [sampleRate])

  // Pre-warm on mount so the first `start()` doesn't pay the AudioWorklet
  // module load cost (which can take >1s on cold cache and led to the actor
  // recorder appearing to hang on first click). The mock recorder also runs
  // a tiny artificial init so e2e specs see the disabled→enabled transition.
  useEffect(() => {
    let cancelled = false
    const r = ensureRecorder()
    void (async () => {
      try {
        if (typeof r.prewarm === 'function') {
          await r.prewarm()
        }
      } catch (err) {
        // Permission denial here is expected on browsers that gate getUserMedia
        // until a user gesture; surface in `error` but still allow start() to
        // try again on click.
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)))
      } finally {
        if (!cancelled) setIsReady(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [ensureRecorder])

  const start = useCallback(async () => {
    setError(null)
    const r = ensureRecorder()
    try {
      await r.start()
      setIsRecording(true)
      // Pump live peak via RAF — Recorder's worklet pushes peak per chunk,
      // but we don't want to re-render on every frame; a 60Hz mirror is fine.
      const tick = () => {
        if (!recorderRef.current) return
        setLivePeak(recorderRef.current.currentPeak)
        peakRafRef.current = requestAnimationFrame(tick)
      }
      peakRafRef.current = requestAnimationFrame(tick)
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      setError(e)
      setIsRecording(false)
      throw e
    }
  }, [ensureRecorder])

  const stop = useCallback(async (): Promise<RecorderResult> => {
    const r = recorderRef.current
    if (!r) throw new Error('useRecorder.stop: not started')
    if (peakRafRef.current !== null) {
      cancelAnimationFrame(peakRafRef.current)
      peakRafRef.current = null
    }
    try {
      const result = await r.stop()
      setIsRecording(false)
      // Release the recorder so a fresh AudioContext is allocated for the
      // next take — sidesteps Chrome's "AudioContext was not allowed to
      // start" auto-suspend after long-idle.
      recorderRef.current = null
      return result
    } catch (err) {
      setIsRecording(false)
      throw err
    }
  }, [])

  const cancel = useCallback(() => {
    const r = recorderRef.current
    if (!r) return
    if (peakRafRef.current !== null) {
      cancelAnimationFrame(peakRafRef.current)
      peakRafRef.current = null
    }
    r.cancel()
    setIsRecording(false)
    setLivePeak(0)
    recorderRef.current = null
  }, [])

  const resetPeak = useCallback(() => setLivePeak(0), [])

  useEffect(() => {
    return () => {
      if (peakRafRef.current !== null) cancelAnimationFrame(peakRafRef.current)
      if (cancelOnUnmount) recorderRef.current?.cancel()
      recorderRef.current = null
    }
  }, [cancelOnUnmount])

  return { start, stop, cancel, isRecording, isReady, livePeak, error, resetPeak }
}
